import { useTranslator } from '@translator-app/react';
import classNames from 'classnames';
import DayJS from 'dayjs';
import { GetServerSidePropsContext } from 'next';
import { useRouter } from 'next/router';
import React from 'react';

import { BackLink } from '../../../../components/atoms/back-link';
import { Button, LinkButton } from '../../../../components/atoms/button';
import { Heading, SubHeading } from '../../../../components/atoms/heading';
import { Image } from '../../../../components/atoms/image';
import { MobileActionContainer } from '../../../../components/atoms/mobile-action-container';
import { Paragraph } from '../../../../components/atoms/paragraph';
import { ErrorFallback } from '../../../../components/organisms/error-boundary';
import { NotFound } from '../../../../components/organisms/not-found';
import { OrderHint } from '../../../../components/organisms/order-hint';
import { FallbackPageTemplate } from '../../../../components/templates/fallback-page-template';
import { PageTemplate } from '../../../../components/templates/page-template';
import { ShopPageTemplate } from '../../../../components/templates/shop-page-template';
import { ShoppingCart, useApplicationState } from '../../../../contexts/application-state-context';
import { useLanguageContext } from '../../../../contexts/language-context';
import {
  AllergensEnumType,
  NutritionalInformationObjectType,
  OrderObjectType,
  OrganisationObjectType,
  ProductObjectType,
  ReservationObjectType,
  ReservationStatusEnumType,
  ShopObjectType,
} from '../../../../generated/graphql';
import { MinimalServerError } from '../../../../types';
import { getOrganisationAndShop } from '../../../../utils/api';
import { formatCurrency } from '../../../../utils/currency';
import { formatDateTimeString } from '../../../../utils/date-time';
import { ImageFitMode } from '../../../../utils/imgix';
import { formatAmount } from '../../../../utils/number';
import {
  getLocalisedString,
  getProductCollectionItemsSummary,
  getReservationPrices,
  getReservationProductImages,
  getReservationProductsName,
} from '../../../../utils/product';
import { captureException } from '../../../_app';
import { Sentry } from '../../../_app';

export interface AllergenIconProps {
  allergenType: string;
}

function AllergenIcon(props: AllergenIconProps) {
  let { allergenType } = props;
  let { translate } = useTranslator();

  let localisedAllergenName = translate(`allergenEnum.${allergenType}`);

  return (
    <div className="h-24 w-24 flex flex-col items-center justify-center border bg-white rounded mb-4 mr-4">
      <div className="w-16 h-16 p-2 flex justify-center items-center">
        <img
          src={`/allergen-icons/${allergenType}.svg`}
          alt={localisedAllergenName}
          title={localisedAllergenName}
          className="max-h-full max-w-full"
        />
      </div>
      <div
        className={classNames('font-medium uppercase', {
          'text-sm': localisedAllergenName.length < 8,
          'text-xs': localisedAllergenName.length >= 8,
        })}
      >
        {localisedAllergenName}
      </div>
    </div>
  );
}

function ReservationContentOverview(props: { reservation: ReservationObjectType }) {
  let { reservation } = props;
  let { translate } = useTranslator();

  let allergens = React.useMemo(() => {
    let allergensSet: Set<AllergensEnumType> = new Set();
    for (let product of reservation.vendingProducts) {
      const productDetails = product.product;
      if (productDetails) {
        for (let allergen of productDetails.allergens) {
          allergensSet.add(allergen);
        }
      }
    }
    return Array.from(allergensSet);
  }, [reservation]);

  return (
    <div>
      {allergens.length > 0 && (
        <div className="w-full">
          <SubHeading>{translate('product.allergens')}</SubHeading>
          <div className="mt-2 flex flex-wrap">
            {allergens.map((allergen) => {
              return <AllergenIcon allergenType={allergen} key={allergen} />;
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function NutritionalInformationTable(props: { nutritionalInformation: NutritionalInformationObjectType }) {
  let { nutritionalInformation } = props;

  const indentedStyle = {
    maxWidth: '27.5rem',
  };

  return (
    <div className="w-full">
      <SubHeading>Voedingstabel</SubHeading>
      <div className="mt-2">
        <div className="font-bold mb-1">Op basis van ±100g</div>
        <div className="flex justify-between max-w-md">
          <div>Energetische waarde (kCal)</div>
          <div>{formatAmount(nutritionalInformation.energy, ' kCal')}</div>
        </div>
        <div className="flex justify-between max-w-md">
          <div>Vetten</div>
          <div>{formatAmount(nutritionalInformation.fat / 100, ' g', 2, true)}</div>
        </div>
        <div className="ml-2 flex justify-between" style={indentedStyle}>
          <div>Waarvan verzadigd</div>
          <div>{formatAmount(nutritionalInformation.saturatedFat / 100, ' g', 2, true)}</div>
        </div>
        {nutritionalInformation.cholesterol > 0 && (
          <div className="flex justify-between max-w-md">
            <div>Cholesterol</div>
            <div>{formatAmount(nutritionalInformation.cholesterol / 100, ' g', 2, true)}</div>
          </div>
        )}
        <div className="flex justify-between max-w-md">
          <div>Koolhydraten</div>
          <div>{formatAmount(nutritionalInformation.fiber / 100, ' g', 2, true)}</div>
        </div>
        <div className="ml-2">
          <div className="flex justify-between" style={indentedStyle}>
            <div>Suiker</div>
            <div>{formatAmount(nutritionalInformation.sugars / 100, ' g', 2, true)}</div>
          </div>
          {nutritionalInformation.addedSugars > 0 && (
            <div className="flex justify-between" style={indentedStyle}>
              <div>Toegevoegde suiker</div>
              <div>{formatAmount(nutritionalInformation.addedSugars / 100, ' g', 2, true)}</div>
            </div>
          )}
        </div>
        {nutritionalInformation.fiber > 0 && (
          <div className="flex justify-between max-w-md">
            <div>Vezels</div>
            <div>{formatAmount(nutritionalInformation.carbohydrate / 100, ' g', 2, true)}</div>
          </div>
        )}
        <div className="flex justify-between max-w-md">
          <div>Eiwitten</div>
          <div>{formatAmount(nutritionalInformation.protein / 100, ' g', 2, true)}</div>
        </div>
        <div className="flex justify-between max-w-md">
          <div>Zout</div>
          <div>{formatAmount(nutritionalInformation.salt / 100, ' g', 2, true)}</div>
        </div>
      </div>
    </div>
  );
}

function ProductDetails(props: { product: ProductObjectType; includeHeading?: boolean }) {
  let { product, includeHeading = false } = props;
  let { language } = useLanguageContext();
  let { translate } = useTranslator();

  let productName = React.useMemo(() => {
    return getLocalisedString(product.name, language) || 'Removed product';
  }, [product]);

  let productDescription = React.useMemo(() => {
    return getLocalisedString(product.description, language);
  }, [product]);

  let allergens = product.allergens;
  let hasIncludedProducts = !!(product.includedProducts && product.includedProducts.length);

  return (
    <div>
      <div className="my-4">
        {includeHeading && <SubHeading>{productName}</SubHeading>}
        <Paragraph>{productDescription}</Paragraph>
      </div>
      {hasIncludedProducts && (
        <div className="my-4">
          <Paragraph>{getProductCollectionItemsSummary(product.includedProducts)}</Paragraph>
        </div>
      )}
      {allergens.length > 0 && (
        <div className="mt-4">
          <SubHeading>{translate('product.allergens')}</SubHeading>
          <div className="flex flex-wrap mt-2">
            {allergens.map((allergen) => {
              return <AllergenIcon allergenType={allergen} key={allergen} />;
            })}
          </div>
        </div>
      )}
      {product.hasNutritionalInformation && (
        <NutritionalInformationTable nutritionalInformation={product.nutritionalInformation} />
      )}
      {hasIncludedProducts && (
        <SubProductsSection products={product.includedProducts.map((p) => p.product).filter((p) => !!p) as ProductObjectType[]} />
      )}
    </div>
  );
}

const SubProductsSection: React.FC<{ products: Array<ProductObjectType> }> = React.memo((props) => {
  let { products } = props;
  let [selectedProductIndex, setSelectedProductIndex] = React.useState(0);

  const filteredProducts = products.filter((p) => !!p);
  const selectedProduct = filteredProducts[selectedProductIndex];

  return (
    <div className="bg-white p-4">
      <div className="flex justify-start flex-wrap border-b pb-2">
        {filteredProducts.map((product, i) => {
          let localisedName = getLocalisedString(product.name);

          return (
            <div key={product._id} className="mb-2 mr-2">
              {selectedProductIndex === i ? (
                <div className="block relative font-medium border rounded text-gray-700 whitespace-no-wrap px-8 py-1">
                  {localisedName}
                </div>
              ) : (
                <Button
                  onPress={() => {
                    setSelectedProductIndex(i);
                  }}
                  disabled={selectedProductIndex === i}
                  appearance="secondary"
                >
                  {localisedName}
                </Button>
              )}
            </div>
          );
        })}
      </div>
      {!!selectedProduct && <ProductDetails product={selectedProduct} includeHeading />}
    </div>
  );
});

interface ProductPageProps {
  reservation: ReservationObjectType;
  organisation: OrganisationObjectType;
  shop: ShopObjectType;
}

function ProductPage(props: ProductPageProps) {
  let { reservation, shop, organisation } = props;
  let router = useRouter();
  let { addToCart } = useApplicationState();
  let { language } = useLanguageContext();
  let { translate } = useTranslator();
  let [selectedImage, setSelectedImage] = React.useState(0);

  let name = React.useMemo(() => {
    return getReservationProductsName(reservation, language) || 'Removed product';
  }, [reservation]);

  let prices = React.useMemo(() => {
    return getReservationPrices(reservation);
  }, [reservation]);

  let images = React.useMemo(() => {
    return getReservationProductImages(reservation);
  }, [reservation]);

  let pickupDateTime = React.useMemo(() => {
    return DayJS(Date.now()).add(shop.vendingPickupTime, 'hour');
  }, [shop]);

  let showReservationContent = React.useMemo(() => {
    if (reservation.vendingProducts.length > 1) {
      return true;
    } else {
      return reservation.vendingProducts[0].amount > 1;
    }
  }, [reservation]);

  const isSold = reservation.status !== ReservationStatusEnumType.Reserved;
  return (
    <div className="max-w-screen-xl mx-auto p-8">
      <div className="flex mb-4">
        <BackLink
          href="/[organisation]/[shopId]"
          as={`${`/${organisation.pathname}/${shop._id}`}`}
          label={translate('general.goBack')}
        />
      </div>
      <div className="md:flex mt-2">
        {!!images.length && (
          <div className="mb-4 md:w-1/2 lg:w-1/3 md:block flex justify-center">
            <div>
              <Image
                url={images[selectedImage]}
                width={400}
                height={400}
                fitMode={ImageFitMode.Crop}
                sizes="(min-width: 1024px) 400px, 800px"
                alt={name}
                className="w-full h-auto"
              />

              {images.length > 1 && (
                <div className="flex flex-wrap">
                  {images.map((image, i) => {
                    return (
                      <div
                        className="mr-2 mt-2 cursor-pointer"
                        onClick={() => {
                          setSelectedImage(i);
                        }}
                        key={i}
                      >
                        <Image
                          url={image}
                          sizes="200px"
                          width={200}
                          height={200}
                          fitMode={ImageFitMode.Crop}
                          alt={name}
                          className="w-24 h-24"
                        />
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}
        <div
          className={classNames({
            'md:ml-8 md:w-1/2 lg:w-2/3': !!images.length,
            'w-full': !images.length,
          })}
        >
          <div className="border-b pb-4">
            <Heading>{name}</Heading>
            <div className="font-medium text-gray-600">
              {translate('order.pickupAtDeadline', {
                pickupDateTime: formatDateTimeString(pickupDateTime, language),
              })}
            </div>

            <div className="my-2">
              {prices.discountAmount > 0 && (
                <div className="text-lg text-gray-500 line-through">{formatCurrency(prices.totalPrice)}</div>
              )}
              <div className="text-2xl text-gray-700 mt-2 mb-4">{formatCurrency(prices.totalPriceWithDiscount)}</div>
            </div>

            <MobileActionContainer>
              <div className="flex flex-col lg:w-64">
                <Button
                  disabled={isSold}
                  onPress={() => {
                    if (isSold) return;

                    addToCart([reservation]);
                    router.push(
                      '/[organisation]/[shopId]/order/shopping-cart',
                      `/${organisation.pathname}/${shop._id}/order/shopping-cart`
                    );
                  }}
                  appearance="primary"
                  size="lg"
                >
                  {translate(isSold ? 'product.sold' : 'product.orderButtonText')}
                </Button>
              </div>
            </MobileActionContainer>
          </div>

          {showReservationContent && (
            <div className="my-4">
              <Paragraph>{getProductCollectionItemsSummary(reservation.vendingProducts)}</Paragraph>
            </div>
          )}

          {reservation.vendingProducts.length > 1 ? (
            <div>
              <ReservationContentOverview reservation={reservation} />
              <SubProductsSection products={reservation.vendingProducts.map((p) => p.product).filter((p) => !!p) as ProductObjectType[]} />
            </div>
          ) : reservation.vendingProducts[0]?.product ? (
            <ProductDetails product={reservation.vendingProducts[0].product} />
          ) : null}
        </div>
      </div>
    </div>
  );
}

interface PageProps {
  organisation: OrganisationObjectType;
  shop: ShopObjectType;
  reservations: Array<ReservationObjectType>;
  shoppingCart: ShoppingCart;
  order?: OrderObjectType;
  error?: MinimalServerError;
}

function PageContent(props: PageProps) {
  let { shop, organisation } = props;
  let router = useRouter();
  let reservationId = router.query.reservationId;
  let { reservations } = useApplicationState();
  let { translate } = useTranslator();

  let foundReservation = React.useMemo(() => {
    return reservations.find((r) => r._id === reservationId);
  }, [reservations]);

  if (foundReservation) {
    return <ProductPage shop={shop} reservation={foundReservation} organisation={organisation} />;
  } else {
    return (
      <div className="max-w-screen-xl p-8 mx-auto">
        <Heading>{translate('product.noLongerAvailable')}</Heading>
        <div className="my-8">
          <LinkButton
            appearance="primary"
            size="lg"
            href="/[organisation]/[shopId]"
            as={`/${organisation.pathname}/${shop._id}`}
          >
            <div className="text-center">{translate('products.viewOurAvailableProducts')}</div>
          </LinkButton>
        </div>
      </div>
    );
  }
}

export default function Index(props: PageProps) {
  let { organisation, shop, shoppingCart, order, reservations, error } = props;

  if (error) {
    return (
      <FallbackPageTemplate>
        <ErrorFallback error={error} />
      </FallbackPageTemplate>
    );
  }

  if (!organisation) {
    return (
      <PageTemplate>
        <NotFound />
      </PageTemplate>
    );
  }

  return (
    <ShopPageTemplate
      organisation={organisation}
      shop={shop}
      shoppingCart={shoppingCart}
      order={order}
      reservations={reservations}
    >
      <OrderHint />
      <PageContent {...props} />
    </ShopPageTemplate>
  );
}

export async function getServerSideProps(ctx: GetServerSidePropsContext) {
  try {
    let { shopId } = ctx.query;
    let organisationData = await getOrganisationAndShop(ctx, shopId as string);

    if (!organisationData) {
      return {
        props: {},
      };
    } else {
      return {
        props: {
          organisation: organisationData.organisation,
          shop: organisationData.shop,
          reservations: organisationData.reservations,
          shoppingCart: organisationData.shoppingCart,
          order: organisationData.order,
        },
      };
    }
  } catch (err) {
    const eventId = captureException(err);
    await Sentry.flush();

    return {
      props: {
        error: {
          message: err.message,
          eventId,
        },
      },
    };
  }
}
